import type { ResourceConfig } from '../interfaces/ResourceConfig';
import { getRequestContextManager } from '../server/getRequestContextManager';
import { mergeResourceConfig } from './mergeResourceConfig';

/**
 * @group Functions
 * 
 * @summary
 * <blockquote>Resource store for managing ResourceConfig</blockquote>
 * 
 * @remarks
 * This store provides different storage mechanisms for server and client:
 * 
 * **Server (Node.js):**
 * - Uses AsyncLocalStorage for request-scoped isolation
 * - Each HTTP request has its own config stack
 * - Configs are merged from the stack (later configs override earlier ones)
 * - No interference between concurrent requests
 * 
 * **Client (Browser):**
 * - Uses component-scoped stack via ClientResourceProvider
 * - Configs pushed during render, popped on unmount
 * - Managed by React component lifecycle
 * 
 * The store exports a unified interface that works correctly in both environments.
 */

/**
 * Merge all configs in stack (bottom to top, later configs override earlier ones)
 * 
 * @param stack - Array of ResourceConfig objects to merge
 * @returns Merged ResourceConfig or null if stack is empty
 * 
 * @private
 */
function mergeConfigStack(stack: ResourceConfig[]): ResourceConfig | null {
  if (stack.length === 0) {
    return null;
  }

  // Start with first config
  let merged: ResourceConfig = { ...stack[0] };

  // Merge each subsequent config using the centralized merge function
  for (let i = 1; i < stack.length; i++) {
    merged = mergeResourceConfig(merged, stack[i]);
  }

  return merged;
}

/**
 * Resource store with server/client branching

This store implements:
- AsyncLocalStorage-based stack on server (request-scoped)
- Component-based stack on client (via ClientResourceProvider)
 * @internal
 */
export const resourceStore = {
  /**
   * Register a new ResourceConfig
   * 
   * **Server:** Adds to request-scoped AsyncLocalStorage stack
   * **Client:** No-op (use ClientResourceProvider instead)
   * 
   * @param config - The ResourceConfig to register
   * @param key - Unique identifier for this config (auto-generated if not provided)
   */
  register(config: ResourceConfig, key?: string): void {
    if (typeof window === 'undefined') {
      // Server: use AsyncLocalStorage
      const requestContext = getRequestContextManager();
      const configKey = key || `config-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      requestContext.push(configKey, config);
    }
    // Client registration happens via ClientResourceProvider
  },

  /**
   * Get the current ResourceConfig (merged from stack)
   * 
   * **Server:** Merges all configs in request-scoped AsyncLocalStorage stack
   * **Client:** Returns null (client should use getMergedConfig() directly)
   * 
   * @returns Merged ResourceConfig or null if no configs are registered
   */
  get(): ResourceConfig | null {
    if (typeof window === 'undefined') {
      // Server: merge all configs in request stack
      const requestContext = getRequestContextManager();
      const stack = requestContext.getStack();
      return mergeConfigStack(stack);
    } else {
      // Client: handled by getMergedConfig() which uses clientStack
      return null;
    }
  },

  /**
   * Clear all ResourceConfigs
   * 
   * **Server:** Clears request-scoped AsyncLocalStorage context
   * **Client:** No-op (clearing happens via ClientResourceProvider unmount)
   */
  clear(): void {
    if (typeof window === 'undefined') {
      const requestContext = getRequestContextManager();
      requestContext.clear();
    }
    // Client clearing happens via ClientResourceProvider unmount
  }
}; 